import { PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import {
  CreateInventoryMeterReadingGroupDataArg,
  useCreateInventoryMeterReadingGroup,
} from 'hooks/api/reading-groups/useCreate';
import { useInventoryMeterReadingGroups } from 'hooks/api/reading-groups/useFind';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReadingStepContent as messages } from 'routes/inventorize_meter/steps/reading/messages';
import { useStore as useInventorizeStore } from 'routes/inventorize_meter/useStore';
import { InventoryMeterReadingGroup } from '@enmon/commons/lib/tools';
import { Select } from './Select';
import { filterOption } from './utils/filterOption';

interface ReadingGroupSelectProps {
  value?: number;
  onChange?: (value: number) => void;
}

export const ReadingGroupSelect: FC<ReadingGroupSelectProps> = ({ value, onChange }) => {
  const intl = useIntl();
  const { location } = useInventorizeStore();

  const [groupName, setGroupName] = useState('');

  const { data: groupResponse, isLoading: isFetchingReadingGroups } = useInventoryMeterReadingGroups(
    {
      filter: {
        sortField: 'name' satisfies keyof InventoryMeterReadingGroup,
        sortOrder: 'ascend',
        location: location?.id,
      },
    },
    { enabled: !!location?.id },
  );
  const groups = groupResponse?.data ?? [];

  const { mutate: createNewGroup, isLoading: isCreatingReadingGroup } = useCreateInventoryMeterReadingGroup({
    onSuccess: createdGroup => {
      if (onChange && createdGroup?.id) {
        onChange(createdGroup.id);
      }
      setGroupName('');
    },
  });

  const selectDropdown = menu => {
    const newGroup: CreateInventoryMeterReadingGroupDataArg = {
      name: groupName,
      description: '',
      technology: null,
    };

    return (
      <>
        {menu}
        {groupName && !groups.some(group => group?.name?.toLowerCase() === groupName.toLowerCase()) && (
          <Button
            block
            icon={<PlusOutlined />}
            loading={isCreatingReadingGroup}
            type="text"
            onClick={() => createNewGroup(newGroup)}
          >
            <FormattedMessage {...messages.createReadingGroup} values={{ groupName }} />
          </Button>
        )}
      </>
    );
  };

  return (
    <Select
      allowClear
      showSearch
      className="w-100"
      dropdownRender={selectDropdown}
      filterOption={filterOption}
      loading={isFetchingReadingGroups || isCreatingReadingGroup}
      options={groups.map(group => ({ value: group.id, label: group.name }))}
      placeholder={intl.formatMessage(messages.readingGroupPlaceholder)}
      value={value}
      onChange={onChange}
      onSearch={setGroupName}
    />
  );
};
